// Tailor GP correlogram — the high-D pairwise matrix, every cell a SMOOTH
// residual-GP slice (FXC-9009).
//
// The three high-D complaints this replaces (the raw-binned SurfaceCorrelogram):
//   1. "Still horrible spikey bits" — each old cell BINNED the raw scatter
//      (32×32) into a spiky surface. Here each cell is a smooth GP SLICE of the
//      baked field over that cell's (axisX, axisY) pair — one field fetch, then
//      a cheap client-side `sliceGpField` per cell (<1ms each).
//   2. "Compatible shows data, corrected shows none" — the corrected evidence
//      mode transported points to a ~6-D held slice and trust-gated them, so at
//      a deep slice ~0 survived → an empty surface. The GP surface is defined
//      EVERYWHERE, so the SURFACE is never empty; the evidence mode only affects
//      the projected run POINTS, never the surface.
//   3. "Smart way to slide between held conditions" — ONE shared held-condition
//      strip above the matrix (reusing TailorGpSurface's slider UX). A slider per
//      varying axis; each cell holds the axes that are NOT its own pair-axes at
//      the shared values. Geometry axes render as disabled chips. Dragging
//      reslices ALL cells in real time (client-side, no backend).
//
// The matrix is scoped to the field's `axes_varying` (~7 axes → C(7,2)=21 cells),
// not all 12 columns. Rendering ~21 small 3D surfaces uses a SINGLE WebGL
// renderer with scissored per-cell viewports (mirrors SurfaceCorrelogram) so we
// never exhaust the browser's ~8–16 WebGL-context budget. The canvas is
// render-on-demand and explicitly releases its WebGL context on teardown.
//
// When the baked field is unavailable the caller falls back to the existing
// binned SurfaceCorrelogram (via onUnavailable / the parent's renderCorrelogram).

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchGpField } from "../../api/composite";
import {
  applyEvidenceMode,
  axisMidpoint,
  buildSurfaceMeshData,
  convexHull2d,
  defaultHeld,
  fieldAxisDomains,
  rampColorRgb,
  sliceGpField,
  surfaceHeight,
  SURFACE_LAYOUT,
  type AxisDomain,
  type GpEvidenceMode,
  type GpFieldAvailable,
  type SliceResult,
  type SurfaceMeshData,
} from "./gpField";
import {
  buildTransportStalks,
  chronoRankMap,
  clusterEras,
  filterRowsByBudget,
  filterRowsByEras,
  formatTick,
  groupEras,
  makeTextSprite,
  projectRuns,
  trainingPointColors,
  transportStats,
  type EraGroup,
  type ProjectedRun,
  type TransportStalk,
} from "./TailorGpSurface";
import { Icons } from "../thread-v4/icons";

type ThreeModule = typeof import("three");

const CELL_LAYOUT = SURFACE_LAYOUT; // shared world scale (one framing camera)
const CELL_SLICE_RES = 24; // per-cell slice resolution — smooth but light for ~21 cells
const CELL_VIEW = { pitch: 0.62, yaw: -0.52 };

// Re-export the shared, unit-tested surface-mesh builder so existing correlogram
// tests keep importing it from here; the geometry itself lives in gpField.ts and
// is shared with TailorGpSurface (one provably-correct path).
export { buildSurfaceMeshData, type SurfaceMeshData };

// A cell's plotted pair. Upper-triangle: axisX before axisY in the varying order.
export interface CellPair {
  key: string;
  axisX: string;
  axisY: string;
}

/** The upper-triangle pairwise matrix over the varying axes (C(k,2) cells). */
export function varyingPairs(varyingAxes: string[]): CellPair[] {
  const out: CellPair[] = [];
  for (let i = 0; i < varyingAxes.length; i += 1) {
    for (let j = i + 1; j < varyingAxes.length; j += 1) {
      const axisX = varyingAxes[i]!;
      const axisY = varyingAxes[j]!;
      out.push({ key: `${axisX}|${axisY}`, axisX, axisY });
    }
  }
  return out;
}

// ── axis ordering by influence (FXC-9009): the matrix reads top-left-first ───
// field.axes_varying arrives in grid STORAGE order (mach first because the bake
// serialises that way) — meaningless to a reader. Rank the axes by |pearson r|
// against the selected output over the in-hand rows instead, so the first
// pairs are the ones that matter most for that output.

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function pearsonR(rows: Record<string, unknown>[], xKey: string, yKey: string): number | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const row of rows) {
    const x = numericValue(row[xKey]);
    const y = numericValue(row[yKey]);
    if (x === null || y === null) continue;
    xs.push(x);
    ys.push(y);
  }
  if (xs.length < 3) return null;
  const mx = xs.reduce((s, v) => s + v, 0) / xs.length;
  const my = ys.reduce((s, v) => s + v, 0) / ys.length;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const a = xs[i]! - mx;
    const b = ys[i]! - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  return dx > 0 && dy > 0 ? num / Math.sqrt(dx * dy) : null;
}

export interface AxisInfluence {
  axis: string;
  /** Pearson r against the selected output, or null when not measurable
   *  (<3 overlapping finite pairs / constant axis). */
  r: number | null;
}

/**
 * Rank axes by measured influence on the output: |pearson r| descending over
 * the in-hand rows. Deterministic: unmeasurable axes (r null) sink to the end,
 * and ties keep the incoming (storage) order.
 */
export function rankAxesByInfluence(
  rows: Record<string, unknown>[] | undefined,
  axes: string[],
  quantity: string,
): AxisInfluence[] {
  const scored = axes.map((axis, index) => ({
    axis,
    r: rows?.length && quantity ? pearsonR(rows, axis, quantity) : null,
    index,
  }));
  scored.sort((a, b) => {
    const ra = a.r === null ? -1 : Math.abs(a.r);
    const rb = b.r === null ? -1 : Math.abs(b.r);
    if (rb !== ra) return rb - ra;
    return a.index - b.index; // stable tie-break: storage order
  });
  return scored.map(({ axis, r }) => ({ axis, r }));
}

/**
 * Off-plane held values for one cell: every varying axis EXCEPT the cell's own
 * (axisX, axisY) pair, taken from the shared held strip. (Geometry/fixed axes
 * collapse to cardinality 1 in the field so they never interpolate.)
 */
export function cellHeld(
  shared: Record<string, number>,
  axisX: string,
  axisY: string,
): Record<string, number> {
  const held: Record<string, number> = {};
  for (const [axis, value] of Object.entries(shared)) {
    if (axis !== axisX && axis !== axisY) held[axis] = value;
  }
  return held;
}

// ── per-cell scene content built from a GP slice ─────────────────────────────
type CellScene = {
  scene: InstanceType<ThreeModule["Scene"]>;
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>;
  group: InstanceType<ThreeModule["Group"]>;
  disposables: Array<{ dispose(): void }>;
  slice: SliceResult;
  /** The cell's runs at build time, in instance order — right-click picking
   * maps a pointer position back to a run id through these. */
  pickRuns: ProjectedRun[];
};

type CorrelogramRuntime = {
  THREE: ThreeModule;
  renderer: InstanceType<ThreeModule["WebGLRenderer"]>;
  cells: Map<string, CellScene>;
  /** True once a REAL frame landed: non-trivial root size + at least one cell
   *  drawn. Reset on rebuild; the ensure-painted loop retries until it flips. */
  painted: boolean;
  render: () => void;
  dispose: () => void;
};

function buildCellGroup(
  THREE: ThreeModule,
  group: InstanceType<ThreeModule["Group"]>,
  disposables: Array<{ dispose(): void }>,
  slice: SliceResult,
  runs: ProjectedRun[],
  stalks: TransportStalk[] = [],
  hull: Array<{ x: number; y: number }> = [],
  pointColors: Array<[number, number, number]> | null = null,
  axisLabels: { x: string; y: string } | null = null,
): void {
  const { xVals, yVals, zMin, zMax } = slice;
  const py = (v: number) => surfaceHeight(v, zMin, zMax);

  // the Tailor surface: sliced GP z-grid, coloured by z (pure math + THREE glue).
  // Sigma-fade: RGBA vertex colours (itemSize 4) — solid where data-backed,
  // ghost where sigma says extrapolation (trust-surfaces opacity idiom).
  const { positions, colors, alphas, indices } = buildSurfaceMeshData(slice);
  if (indices.length) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    const rgba = new Float32Array((positions.length / 3) * 4);
    for (let vi = 0; vi < positions.length / 3; vi += 1) {
      rgba[vi * 4] = colors[vi * 3]!;
      rgba[vi * 4 + 1] = colors[vi * 3 + 1]!;
      rgba[vi * 4 + 2] = colors[vi * 3 + 2]!;
      rgba[vi * 4 + 3] = alphas[vi] ?? 1;
    }
    geo.setAttribute("color", new THREE.Float32BufferAttribute(rgba, 4));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true, side: THREE.DoubleSide, transparent: true, roughness: 0.62, metalness: 0.02,
    });
    group.add(new THREE.Mesh(geo, mat));
    disposables.push(geo, mat);
  }

  // base plate for spatial reference
  const plateGeo = new THREE.PlaneGeometry(CELL_LAYOUT.width * 1.04, CELL_LAYOUT.depth * 1.04);
  const plateMat = new THREE.MeshBasicMaterial({ color: 0x333842, transparent: true, opacity: 0.08, side: THREE.DoubleSide });
  const plate = new THREE.Mesh(plateGeo, plateMat);
  plate.rotation.x = -Math.PI / 2;
  plate.position.y = -CELL_LAYOUT.height / 2 - 0.02;
  group.add(plate);
  disposables.push(plateGeo, plateMat);

  // plane mappers shared by the run spheres and the transport stalks
  const xLo = xVals[0] ?? 0;
  const xHi = xVals[xVals.length - 1] ?? 1;
  const yLo = yVals[0] ?? 0;
  const yHi = yVals[yVals.length - 1] ?? 1;
  const sx = (x: number) => -CELL_LAYOUT.width / 2 + ((x - xLo) / ((xHi - xLo) || 1)) * CELL_LAYOUT.width;
  const sz = (y: number) => -CELL_LAYOUT.depth / 2 + ((y - yLo) / ((yHi - yLo) || 1)) * CELL_LAYOUT.depth;

  // Per-cell axes: front/right base edges with min/max value ticks plus the
  // axis names, and a min/max pair on the value edge — the small-cell version
  // of TailorGpSurface's addAxis. Sprites are canvas textures (null under
  // jsdom — cells then keep bare lines, matching the single-surface guard).
  {
    const hw = CELL_LAYOUT.width / 2;
    const hd = CELL_LAYOUT.depth / 2;
    const hh = CELL_LAYOUT.height / 2;
    const edgeMat = new THREE.LineBasicMaterial({ color: 0x77808f, transparent: true, opacity: 0.85 });
    disposables.push(edgeMat);
    const edge = (a: [number, number, number], b: [number, number, number]) => {
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(...a), new THREE.Vector3(...b),
      ]);
      group.add(new THREE.Line(geo, edgeMat));
      disposables.push(geo);
    };
    const sprite = (
      text: string,
      position: [number, number, number],
      opts: { height?: number; bold?: boolean } = {},
    ) => {
      const made = makeTextSprite(THREE, text, {
        height: opts.height ?? 0.13,
        bold: opts.bold,
        color: opts.bold ? "#aeb6c4" : "#8b93a1",
      });
      if (!made) return;
      made.sprite.position.set(...position);
      group.add(made.sprite);
      disposables.push(...made.disposables);
    };
    // X axis along the front-bottom edge
    edge([-hw, -hh - 0.02, hd + 0.04], [hw, -hh - 0.02, hd + 0.04]);
    sprite(formatTick(xLo), [-hw, -hh - 0.14, hd + 0.2]);
    sprite(formatTick(xHi), [hw, -hh - 0.14, hd + 0.2]);
    if (axisLabels) sprite(axisLabels.x, [0, -hh - 0.3, hd + 0.3], { bold: true, height: 0.15 });
    // plot-Y axis along the right-bottom edge
    edge([hw + 0.04, -hh - 0.02, -hd], [hw + 0.04, -hh - 0.02, hd]);
    sprite(formatTick(yLo), [hw + 0.22, -hh - 0.14, -hd]);
    sprite(formatTick(yHi), [hw + 0.22, -hh - 0.14, hd]);
    if (axisLabels) sprite(axisLabels.y, [hw + 0.34, -hh - 0.3, 0], { bold: true, height: 0.15 });
    // value axis on the left-back vertical edge
    edge([-hw - 0.04, -hh, -hd - 0.04], [-hw - 0.04, hh, -hd - 0.04]);
    sprite(formatTick(zMin), [-hw - 0.2, -hh, -hd - 0.16]);
    sprite(formatTick(zMax), [-hw - 0.2, hh, -hd - 0.16]);
  }

  // data-support hull: outside this outline no run sits near the held slice —
  // the cell's surface there is pure model.
  if (hull.length >= 3) {
    const hullPts = hull.map((p) => new THREE.Vector3(sx(p.x), -CELL_LAYOUT.height / 2 - 0.01, sz(p.y)));
    const hullGeo = new THREE.BufferGeometry().setFromPoints(hullPts);
    const hullMat = new THREE.LineBasicMaterial({ color: 0xd9a125, transparent: true, opacity: 0.85 });
    group.add(new THREE.LineLoop(hullGeo, hullMat));
    disposables.push(hullGeo, hullMat);
  }

  // transport stalks (corrected mode): raw→corrected verticals, coloured by |Δ|
  // through the shared ramp so long transports stand out per cell.
  if (stalks.length) {
    const maxAbs = Math.max(...stalks.map((s) => Math.abs(s.delta))) || 1;
    const stalkPos: number[] = [];
    const stalkCol: number[] = [];
    for (const stalk of stalks) {
      const wx = sx(stalk.x);
      const wz = sz(stalk.y);
      stalkPos.push(wx, py(stalk.rawValue), wz, wx, py(stalk.correctedValue), wz);
      const [r, g, b] = rampColorRgb(Math.abs(stalk.delta) / maxAbs);
      stalkCol.push(r, g, b, r, g, b);
    }
    const stalkGeo = new THREE.BufferGeometry();
    stalkGeo.setAttribute("position", new THREE.Float32BufferAttribute(stalkPos, 3));
    stalkGeo.setAttribute("color", new THREE.Float32BufferAttribute(stalkCol, 3));
    const stalkMat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.7 });
    group.add(new THREE.LineSegments(stalkGeo, stalkMat));
    disposables.push(stalkGeo, stalkMat);
  }

  // projected runs: each at (axisX, axisY) with its MEASURED value as the height,
  // mapped through the slice's own node range. The evidence mode may filter/scale
  // which points arrive here, but the surface above is drawn regardless.
  // Optional per-run colours (GP training-point highlight, FXC-9009).
  if (runs.length) {
    // A run with Monte-Carlo members is drawn ONLY as its member cloud — the
    // cloud is the point, exactly like the 2-D and 3-D views. Runs without
    // members keep the opaque sphere. Picking is untouched: pickRuns projects
    // from run data (the transported mean), not from any mesh.
    const hasColors = !!pointColors && pointColors.length === runs.length;
    const solid: number[] = [];
    let memberCount = 0;
    for (let k = 0; k < runs.length; k += 1) {
      if (runs[k]!.mc?.length) memberCount += runs[k]!.mc!.length;
      else solid.push(k);
    }
    const dummy = new THREE.Object3D();
    const instColor = new THREE.Color();
    if (solid.length) {
      const sphereGeo = new THREE.SphereGeometry(0.03, 8, 6);
      const sphereMat = new THREE.MeshStandardMaterial({ color: hasColors ? 0xffffff : 0xe2e8f0, roughness: 0.5, metalness: 0.05 });
      const mesh = new THREE.InstancedMesh(sphereGeo, sphereMat, solid.length);
      for (let s = 0; s < solid.length; s += 1) {
        const run = runs[solid[s]!]!;
        dummy.position.set(sx(run.x), py(run.value), sz(run.y));
        dummy.updateMatrix();
        mesh.setMatrixAt(s, dummy.matrix);
        if (hasColors) {
          const [r, g, b] = pointColors![solid[s]!]!;
          mesh.setColorAt(s, instColor.setRGB(r, g, b));
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      group.add(mesh);
      disposables.push(sphereGeo, sphereMat, mesh);
    }
    if (memberCount) {
      // One THREE.Points per cell, not an InstancedMesh: the 300 ms held-
      // slider morph rebuilds every cell per animation frame, and filling a
      // flat position buffer is far cheaper than thousands of per-instance
      // matrices — the same primitive the 3-D surface view uses for clouds.
      // depthWrite off: translucent particles against the surface mesh would
      // otherwise sort-fight and flicker as the shared view rotates.
      const memberPositions = new Float32Array(memberCount * 3);
      let cursor = 0;
      for (let k = 0; k < runs.length; k += 1) {
        const run = runs[k]!;
        if (!run.mc?.length) continue;
        for (let member = 0; member < run.mc.length; member += 1) {
          // Deterministic golden-ratio jitter in the cell plane so members
          // spread instead of stacking into a vertical needle.
          const spread = 0.011;
          memberPositions[cursor] = sx(run.x) + (((member * 0.618034) % 1) - 0.5) * 2 * spread;
          memberPositions[cursor + 1] = py(run.mc[member]!);
          memberPositions[cursor + 2] = sz(run.y) + (((member * 0.381966) % 1) - 0.5) * 2 * spread;
          cursor += 3;
        }
      }
      const memberGeo = new THREE.BufferGeometry();
      memberGeo.setAttribute("position", new THREE.Float32BufferAttribute(memberPositions, 3));
      const memberMat = new THREE.PointsMaterial({
        color: 0xe2e8f0,
        size: 0.04,
        transparent: true,
        opacity: 0.42,
        depthWrite: false,
        sizeAttenuation: true,
      });
      const cloud = new THREE.Points(memberGeo, memberMat);
      cloud.renderOrder = 5;
      group.add(cloud);
      // Points has no dispose() of its own — the geometry and material carry
      // the GPU resources; group.clear() detaches the object on rebuild.
      disposables.push(memberGeo, memberMat);
    }
  }
}

/**
 * The scissored WebGL layer: one renderer, one scene per cell, each drawn into
 * the DOM rect of its cell. Reslicing (held change) rebuilds each cell's group
 * in place — the renderer/scenes persist. This is the many-small-surfaces
 * equivalent of TailorGpSurface's single persistent controller.
 */
export function CorrelogramThreeLayer({
  slices,
  runsByKey,
  stalksByKey,
  hullsByKey,
  colorsByKey,
  cellRefs,
  rootRef,
  view,
  axisLabels,
  onPointContextMenu,
}: {
  slices: Map<string, SliceResult>;
  runsByKey: Map<string, ProjectedRun[]>;
  /** Raw→corrected transport stalks per cell (corrected mode only). */
  stalksByKey: Map<string, TransportStalk[]>;
  /** Data-support hull per cell (in-slice runs' convex hull). */
  hullsByKey: Map<string, Array<{ x: number; y: number }>>;
  /** Optional per-cell per-run colours (GP training-point highlight). */
  colorsByKey: Map<string, Array<[number, number, number]>>;
  cellRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  rootRef: React.RefObject<HTMLDivElement | null>;
  view: { pitch: number; yaw: number; zoom?: number };
  /** Human axis captions keyed by axis id, drawn on every cell's axes. */
  axisLabels?: Record<string, string>;
  /** Right-click "Why this point?" — receives the run's SamplePoint id. */
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
}) {
  const runtimeRef = useRef<CorrelogramRuntime | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Latest inputs in refs so the async boot AND any scheduled render always read
  // the current data — the fix for the blank render was an ORDER bug: the build
  // effect ran while the async `import("three")` was still pending (runtime null),
  // bailed, and nothing rebuilt. Boot now builds from these refs the moment the
  // renderer is ready, and a version counter re-triggers builds on data change.
  const viewRef = useRef(view);
  viewRef.current = view;
  const slicesRef = useRef(slices);
  slicesRef.current = slices;
  const runsRef = useRef(runsByKey);
  runsRef.current = runsByKey;
  const stalksRef = useRef(stalksByKey);
  stalksRef.current = stalksByKey;
  const hullsRef = useRef(hullsByKey);
  hullsRef.current = hullsByKey;
  const colorsRef = useRef(colorsByKey);
  colorsRef.current = colorsByKey;
  const axisLabelsRef = useRef(axisLabels);
  axisLabelsRef.current = axisLabels;
  const onPointContextMenuRef = useRef(onPointContextMenu);
  onPointContextMenuRef.current = onPointContextMenu;
  const rafRef = useRef<number | null>(null);

  // Rebuild every cell scene from the CURRENT slices/runs refs, in place.
  const buildCells = useCallback((runtime: CorrelogramRuntime) => {
    const { THREE } = runtime;
    const slicesNow = slicesRef.current;
    const runsNow = runsRef.current;
    const stalksNow = stalksRef.current;
    const hullsNow = hullsRef.current;
    const colorsNow = colorsRef.current;
    for (const [key, cell] of runtime.cells) {
      if (!slicesNow.has(key)) {
        for (const d of cell.disposables) d.dispose();
        cell.scene.clear();
        runtime.cells.delete(key);
      }
    }
    for (const [key, slice] of slicesNow) {
      let cell = runtime.cells.get(key);
      if (!cell) {
        const scene = new THREE.Scene();
        scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.05));
        const dir = new THREE.DirectionalLight(0xffffff, 0.7);
        dir.position.set(3, 6, 2);
        scene.add(dir);
        // Camera + world scale IDENTICAL to TailorGpSurface (which renders fine):
        // fov 42, eye (2.6,2.2,2.6) looking at origin, surface built to CELL_LAYOUT.
        const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
        camera.position.set(2.6, 2.2, 2.6);
        camera.lookAt(0, 0, 0);
        const group = new THREE.Group();
        scene.add(group);
        cell = { scene, camera, group, disposables: [], slice, pickRuns: [] };
        runtime.cells.set(key, cell);
      }
      for (const d of cell.disposables) d.dispose();
      cell.disposables = [];
      cell.group.clear();
      cell.group.rotation.x = viewRef.current.pitch;
      cell.group.rotation.y = viewRef.current.yaw;
      cell.group.scale.setScalar(viewRef.current.zoom ?? 1);
      cell.slice = slice;
      const cellRuns = runsNow.get(key) ?? [];
      cell.pickRuns = cellRuns;
      const labels = axisLabelsRef.current;
      buildCellGroup(
        THREE, cell.group, cell.disposables, slice,
        cellRuns, stalksNow.get(key) ?? [], hullsNow.get(key) ?? [],
        colorsNow.get(key) ?? null,
        {
          x: labels?.[slice.xAxis] ?? slice.xAxis,
          y: labels?.[slice.yAxis] ?? slice.yAxis,
        },
      );
    }
  }, []);

  // Schedule a render on the next frame — so getBoundingClientRect reads the
  // grid AFTER layout settles (cell sizes come from a ResizeObserver a frame
  // later). Coalesces bursts of build/resize/scroll calls into one paint.
  const scheduleRender = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      runtimeRef.current?.render();
    });
  }, []);

  // Belt-and-braces paint guarantee (FXC-9009): retry render on a short capped
  // timeout loop until runtime.painted flips (a REAL frame: non-trivial size +
  // at least one cell drawn). Timeouts (not rAF) so a backgrounded tab still
  // ticks; idempotent — each tick is at most one render.
  const ensureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ensurePainted = useCallback(() => {
    if (ensureTimerRef.current) clearTimeout(ensureTimerRef.current);
    const deadline = performance.now() + 2000;
    const tick = () => {
      ensureTimerRef.current = null;
      const runtime = runtimeRef.current;
      if (!runtime || runtime.painted) return;
      runtime.render();
      if (!runtime.painted && performance.now() < deadline) {
        ensureTimerRef.current = setTimeout(tick, 120);
      }
    };
    ensureTimerRef.current = setTimeout(tick, 0);
  }, []);
  useEffect(() => () => {
    if (ensureTimerRef.current) clearTimeout(ensureTimerRef.current);
    ensureTimerRef.current = null;
  }, []);

  // boot the renderer once, then build + paint from the current refs
  useEffect(() => {
    let disposed = false;
    const boot = async () => {
      try {
        const THREE = await import("three");
        if (disposed || !rootRef.current) return;
        const root = rootRef.current;
        root.querySelectorAll("canvas").forEach((c) => c.remove());
        // Keep one short-lived backbuffer. Retaining every rendered framebuffer
        // becomes extremely expensive for a tall 21-cell matrix and can evict the
        // browser's GPU process while a replay is updating its scenes.
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: "high-performance" });
        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setScissorTest(true);
        renderer.domElement.style.position = "absolute";
        renderer.domElement.style.inset = "0";
        renderer.domElement.style.width = "100%";
        renderer.domElement.style.height = "100%";
        renderer.domElement.style.pointerEvents = "none";
        root.appendChild(renderer.domElement);
        const runtime: CorrelogramRuntime = {
          THREE,
          renderer,
          cells: new Map(),
          painted: false,
          render: () => {},
          dispose: () => {},
        };
        let rendererWidth = 0;
        let rendererHeight = 0;
        runtime.render = () => {
          if (disposed || !rootRef.current) return;
          try {
            const rootRect = rootRef.current.getBoundingClientRect();
            const width = Math.max(1, Math.floor(rootRect.width));
            const height = Math.max(1, Math.floor(rootRect.height));
            if (width !== rendererWidth || height !== rendererHeight) {
              renderer.setSize(width, height, false);
              rendererWidth = width;
              rendererHeight = height;
            }
            renderer.setScissorTest(false);
            renderer.setViewport(0, 0, width, height);
            renderer.clear(true, true, true);
            renderer.setScissorTest(true);
            let drewAny = false;
            // viewport clip: skip cells fully outside the browser viewport (the
            // grid host scrolls, so offscreen rows shouldn't cost a render)
            const viewBottom = typeof window !== "undefined" ? window.innerHeight || Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
            for (const [key, cell] of runtime.cells) {
              const el = cellRefs.current[key];
              if (!el) continue;
              const rect = el.getBoundingClientRect();
              if (rect.width < 4 || rect.height < 4) continue;
              if (rect.bottom < rootRect.top || rect.top > rootRect.bottom || rect.right < rootRect.left || rect.left > rootRect.right) continue;
              if (rect.bottom < 0 || rect.top > viewBottom) continue; // offscreen while scrolling
              const vx = Math.max(0, Math.floor(rect.left - rootRect.left));
              const vy = Math.max(0, Math.floor(height - (rect.bottom - rootRect.top)));
              const vw = Math.max(1, Math.floor(Math.min(rect.right, rootRect.right) - Math.max(rect.left, rootRect.left)));
              const vh = Math.max(1, Math.floor(Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top)));
              renderer.setViewport(vx, vy, vw, vh);
              renderer.setScissor(vx, vy, vw, vh);
              cell.camera.aspect = vw / vh;
              cell.camera.updateProjectionMatrix();
              cell.group.rotation.x = viewRef.current.pitch;
              cell.group.rotation.y = viewRef.current.yaw;
              cell.group.scale.setScalar(viewRef.current.zoom ?? 1);
              renderer.render(cell.scene, cell.camera);
              drewAny = true;
            }
            // a REAL frame: the root grid had size and at least one cell painted
            if (drewAny && rootRect.width >= 40 && rootRect.height >= 40) runtime.painted = true;
            setError(null);
          } catch (err) {
            runtime.painted = false;
            setError(err instanceof Error ? err.message : "3D surface update failed");
          }
        };
        runtime.dispose = () => {
          for (const cell of runtime.cells.values()) {
            for (const d of cell.disposables) d.dispose();
            cell.scene.clear();
          }
          runtime.cells.clear();
          renderer.dispose();
          try {
            renderer.forceContextLoss();
          } catch {
            // Best-effort in test/headless renderers without WEBGL_lose_context.
          }
          renderer.domElement.remove();
        };
        runtimeRef.current = runtime;
        setError(null);
        // Build the cells NOW (boot won this race, so runtime is live) and paint
        // on the next frame once the grid has laid out. A second deferred paint
        // survives the cell-sizing ResizeObserver landing a frame later; the
        // ensure-painted loop then guarantees a real frame within ~2s.
        buildCells(runtime);
        scheduleRender();
        requestAnimationFrame(() => scheduleRender());
        ensurePainted();
      } catch (err) {
        if (!disposed) setError(err instanceof Error ? err.message : "3D renderer unavailable");
      }
    };
    void boot();
    return () => {
      disposed = true;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      runtimeRef.current?.dispose();
      runtimeRef.current = null;
    };
  }, [cellRefs, rootRef, buildCells, scheduleRender, ensurePainted]);

  // (re)build cell scenes whenever the slices/runs change (held drag, rescope).
  // If the renderer isn't ready yet (boot's import still pending) this is a
  // no-op — boot builds from the refs itself, so nothing is lost.
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.painted = false; // new content — a real frame must land again
    try {
      buildCells(runtime);
      scheduleRender();
      ensurePainted();
      setError(null);
    } catch (err) {
      runtime.painted = false;
      setError(err instanceof Error ? err.message : "3D surface update failed");
    }
  }, [slices, runsByKey, stalksByKey, hullsByKey, colorsByKey, buildCells, scheduleRender, ensurePainted]);

  // repaint on scroll / resize (rects move under the fixed canvas).
  // CAPTURE-phase document listener instead of the old walk-up-to-find-the-
  // scroller: it catches EVERY scrolling ancestor (the grid host's own
  // overflow-y, the page, anything between) even when the scroller only
  // becomes scrollable after mount — the prior walk bound once and could miss.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ro = new ResizeObserver(() => scheduleRender());
    ro.observe(root);
    const onAnyScroll = () => scheduleRender();
    document.addEventListener("scroll", onAnyScroll, { capture: true, passive: true });
    scheduleRender();
    return () => {
      ro.disconnect();
      document.removeEventListener("scroll", onAnyScroll, { capture: true });
    };
  }, [rootRef, scheduleRender]);

  // Classic missed-paint causes: hidden tab (rAF suspended) or offscreen mount.
  // Repaint when the document becomes visible / the grid intersects the viewport.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        scheduleRender();
        if (!runtimeRef.current?.painted) ensurePainted();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    const root = rootRef.current;
    const io = typeof IntersectionObserver !== "undefined" && root
      ? new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            scheduleRender();
            if (!runtimeRef.current?.painted) ensurePainted();
          }
        })
      : null;
    if (root && io) io.observe(root);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
    };
  }, [rootRef, scheduleRender, ensurePainted]);

  useEffect(() => { scheduleRender(); }, [view, scheduleRender]);

  // Right-click "Why this point?" — pick the nearest run sphere within a small
  // screen radius of the pointer, inside whichever cell was clicked. Screen-
  // space nearest (project each instance) rather than a raycast: the spheres
  // are ~3 px targets and a strict ray misses what the eye considers a hit.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const handleContextMenu = (event: MouseEvent) => {
      const runtime = runtimeRef.current;
      const callback = onPointContextMenuRef.current;
      if (!runtime || !callback) return;
      for (const [key, cell] of runtime.cells) {
        const el = cellRefs.current[key];
        if (!el || cell.pickRuns.length === 0) continue;
        const rect = el.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) continue;
        if (event.clientY < rect.top || event.clientY > rect.bottom) continue;
        const { THREE } = runtime;
        const { xVals, yVals, zMin, zMax } = cell.slice;
        const xLo = xVals[0] ?? 0;
        const xHi = xVals[xVals.length - 1] ?? 1;
        const yLo = yVals[0] ?? 0;
        const yHi = yVals[yVals.length - 1] ?? 1;
        cell.camera.aspect = rect.width / Math.max(1, rect.height);
        cell.camera.updateProjectionMatrix();
        cell.group.updateMatrixWorld(true);
        const position = new THREE.Vector3();
        let bestId: string | null = null;
        let bestDistance = 14; // px pick radius
        for (const run of cell.pickRuns) {
          position.set(
            -CELL_LAYOUT.width / 2 + ((run.x - xLo) / ((xHi - xLo) || 1)) * CELL_LAYOUT.width,
            surfaceHeight(run.value, zMin, zMax),
            -CELL_LAYOUT.depth / 2 + ((run.y - yLo) / ((yHi - yLo) || 1)) * CELL_LAYOUT.depth,
          );
          cell.group.localToWorld(position);
          position.project(cell.camera);
          const screenX = rect.left + ((position.x + 1) / 2) * rect.width;
          const screenY = rect.top + ((1 - position.y) / 2) * rect.height;
          const distance = Math.hypot(screenX - event.clientX, screenY - event.clientY);
          if (distance < bestDistance) {
            const row = run.row as Record<string, unknown> | undefined;
            const id = row?._row_id ?? row?.id ?? row?.runId;
            if (id != null) {
              bestDistance = distance;
              bestId = String(id);
            }
          }
        }
        if (bestId != null) {
          event.preventDefault();
          event.stopPropagation();
          callback(bestId, event.clientX, event.clientY);
        }
        return;
      }
    };
    root.addEventListener("contextmenu", handleContextMenu);
    return () => root.removeEventListener("contextmenu", handleContextMenu);
  }, [cellRefs, rootRef]);

  return error ? (
    <div style={{ position: "absolute", left: 10, bottom: 10, color: "#fda4af", fontSize: 10, background: "rgba(2,6,23,0.86)", border: "1px solid rgba(248,113,113,0.35)", borderRadius: 7, padding: "5px 7px", zIndex: 5 }} data-testid="gp-correlogram-webgl-error">
      {error}
    </div>
  ) : null;
}

// ── shared held strip (reuses TailorGpSurface's HeldSlider UX / CSS) ──────────
function HeldSlider({
  axis, label, values, value, onChange,
}: {
  axis: string; label: string; values: number[]; value: number; onChange: (v: number) => void;
}) {
  const lo = values[0] ?? 0;
  const hi = values[values.length - 1] ?? 1;
  const step = useMemo(() => {
    let min = Infinity;
    for (let i = 1; i < values.length; i += 1) min = Math.min(min, Math.abs(values[i]! - values[i - 1]!));
    return Number.isFinite(min) && min > 0 ? min : (hi - lo) / 40 || 1;
  }, [values, hi, lo]);
  return (
    <label className="v5-adb-gp-held" data-testid={`gpc-held-${axis}`} title={`${label}: ${lo} → ${hi}`}>
      <span className="v5-adb-gp-held-label">{label}</span>
      <input
        type="range"
        min={lo}
        max={hi}
        step={step}
        value={value}
        data-testid={`gpc-held-slider-${axis}`}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="v5-adb-gp-held-val">{value}</span>
    </label>
  );
}

export const CellTile = memo(function CellTile({
  cellKey, xLabel, yLabel, cellRefs,
}: {
  cellKey: string; xLabel: string; yLabel: string;
  cellRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}) {
  const nodeRef = useCallback((node: HTMLDivElement | null) => {
    cellRefs.current[cellKey] = node;
  }, [cellRefs, cellKey]);
  return (
    <div
      ref={nodeRef}
      data-testid={`gpc-cell-${cellKey}`}
      className="v5-adbc-gpc-cell"
      // square-ish, sized by the grid track (auto-fill fills the row width)
      style={{ width: "100%", aspectRatio: "1 / 1" }}
      title={`${yLabel} vs ${xLabel} · Tailor GP slice`}
    >
      <div className="v5-adbc-gpc-cell-head">
        <span>{yLabel}</span>
        <span className="v5-adbc-gpc-cell-vs">×</span>
        <span>{xLabel}</span>
      </div>
    </div>
  );
});

export interface TailorGpCorrelogramProps {
  projectId: string;
  branch: string;
  quantity: string;
  /** The explorer rows (same allRows the scatter uses) — projected as points. */
  rows?: Record<string, unknown>[];
  /** Human labels per axis id, for the strip + cell captions. */
  axisLabels?: Record<string, string>;
  /** Called when unavailable so the parent can fall back to the binned
   *  correlogram. reason: "no-field" = a real 200 said no baked field (sticky);
   *  "error" = the fetch failed (transient — the parent should re-probe). */
  onUnavailable?: (unavailable: boolean, reason?: "no-field" | "error") => void;
  /** The explorer's Raw/Compatible/Corrected tab state (FXC-9009 item 4).
   *  Powered by the GP itself (applyEvidenceMode): affects the run POINTS in
   *  every cell — filtering (compatible) or transporting onto the shared held
   *  slice (corrected) — and NEVER the surface, which is defined everywhere. */
  evidenceMode?: GpEvidenceMode;
  /** Optional extra per-cell run filter, applied AFTER the evidence mode. */
  runFilter?: (runs: ProjectedRun[], axisX: string, axisY: string) => ProjectedRun[];
}

export function TailorGpCorrelogram({
  projectId, branch, quantity, rows, axisLabels, onUnavailable, evidenceMode = "raw", runFilter,
}: TailorGpCorrelogramProps) {
  const queryEnabled = !!projectId && !!quantity;
  const query = useQuery({
    queryKey: ["composite-gp-field", projectId, branch, quantity],
    queryFn: ({ signal }) => fetchGpField({ projectId, branch, quantity, signal }),
    enabled: queryEnabled,
  });
  const field = query.data;
  const available = field?.available === true;
  const gpField = available ? (field as GpFieldAvailable) : null;

  // Fall back when genuinely unavailable (sticky) or on fetch error (transient
  // — the parent re-probes). Pending/disabled signals NOTHING; this component
  // renders an explicit visible state instead (never a silent null).
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  useEffect(() => {
    if (query.isSuccess) {
      const noField = field?.available === false;
      if (noField) onUnavailableRef.current?.(true, "no-field");
      else onUnavailableRef.current?.(false);
    } else if (query.isError) {
      onUnavailableRef.current?.(true, "error");
    }
  }, [query.isSuccess, query.isError, field]);

  const domains = useMemo(() => (gpField ? fieldAxisDomains(gpField) : []), [gpField]);
  const varyingDomains = useMemo(() => domains.filter((d) => d.varying), [domains]);
  const fixedDomains = useMemo(() => domains.filter((d) => !d.varying), [domains]);
  const varyingAxes = useMemo(() => varyingDomains.map((d) => d.axis), [varyingDomains]);
  // domainByAxis kept for future per-cell axis-range needs; currently the slice
  // handles ranges internally, so we expose the map but don't require it here.
  const _domainByAxis = useMemo(() => {
    const map = new Map<string, AxisDomain>();
    for (const d of domains) map.set(d.axis, d);
    return map;
  }, [domains]);
  void _domainByAxis;

  // ── ONE shared held strip: seeded from the field midpoints, one per axis. ────
  const [held, setHeld] = useState<Record<string, number>>({});
  useEffect(() => {
    if (gpField) setHeld(defaultHeld(gpField));
  }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── influence ordering: most-influential axis first (|pearson r| against the
  //    selected output over the FULL in-hand rows — stable under budget replay),
  //    so the top-left cells are the pairs that matter most. Re-ranks when the
  //    output changes; with no measurable rows it degrades to storage order. ───
  const influence = useMemo(() => rankAxesByInfluence(rows, varyingAxes, quantity), [rows, varyingAxes, quantity]);
  const orderedAxes = useMemo(() => influence.map((item) => item.axis), [influence]);
  const orderedVaryingDomains = useMemo(
    () => orderedAxes
      .map((axis) => varyingDomains.find((d) => d.axis === axis))
      .filter((d): d is AxisDomain => d !== undefined),
    [orderedAxes, varyingDomains],
  );
  const rankedByData = influence.some((item) => item.r !== null);

  // ── axis visibility: hide/show an axis's PAIRS. A hidden axis stays HELD —
  //    its slider remains (every visible cell still holds it); only its
  //    row/column of cells leaves the matrix. At least two axes stay shown. ────
  const [hiddenAxes, setHiddenAxes] = useState<Set<string>>(() => new Set());
  useEffect(() => { setHiddenAxes(new Set()); }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownAxes = useMemo(() => orderedAxes.filter((axis) => !hiddenAxes.has(axis)), [orderedAxes, hiddenAxes]);
  const toggleAxis = useCallback((axis: string) => {
    setHiddenAxes((cur) => {
      const next = new Set(cur);
      if (next.has(axis)) next.delete(axis);
      else if (orderedAxes.length - cur.size > 2) next.add(axis); // keep ≥2 shown
      return next;
    });
  }, [orderedAxes.length]);
  const shownInfluence = useMemo(
    () => influence.filter((item) => !hiddenAxes.has(item.axis)),
    [influence, hiddenAxes],
  );

  const pairs = useMemo(() => varyingPairs(shownAxes), [shownAxes]);
  const label = useCallback((axis: string) => axisLabels?.[axis] ?? axis, [axisLabels]);

  // ── the client-side slices — recomputed on any held change (real time) ───────
  // Every cell holds the axes that aren't its own pair-axes at the shared values.
  const slices = useMemo(() => {
    const map = new Map<string, SliceResult>();
    if (!gpField) return map;
    for (const pair of pairs) {
      map.set(pair.key, sliceGpField(gpField, {
        axisX: pair.axisX,
        axisY: pair.axisY,
        nx: CELL_SLICE_RES,
        ny: CELL_SLICE_RES,
        held: cellHeld(held, pair.axisX, pair.axisY),
      }));
    }
    return map;
  }, [gpField, pairs, held]);

  // ── projected runs per cell — the evidence mode filters (compatible) or
  //    transports (corrected) the POINTS via the GP itself; the SURFACE is the
  //    GP and never blanks (the old binned corrected-mode emptiness is gone).
  //    Corrected mode also yields raw→corrected STALKS per cell so the
  //    transport distance is visible/mistrustable. ─────────────────────────────
  // ── data-era filter: gap-clustered created_at sessions as toggle chips —
  //    hide legacy sweeps / show only Tailor-picked data across ALL cells.
  //    HEURISTIC (gap > 6h = new era); unstamped rows never hidden. ────────────
  const eraData = useMemo(() => clusterEras(rows ?? []), [rows]);
  const [hiddenEras, setHiddenEras] = useState<Set<string>>(() => new Set());
  useEffect(() => { setHiddenEras(new Set()); }, [eraData]);
  const eraRows = useMemo(
    () => (rows ? filterRowsByEras(rows, eraData?.eraByRow ?? null, hiddenEras) : rows),
    [rows, eraData, hiddenEras],
  );
  const eraFiltered = hiddenEras.size > 0 && !!rows && !!eraRows && eraRows.length !== rows.length;

  // ── budget replay (item 5b): first-N runs chronologically across ALL cells,
  //    WITHIN the era-visible set (era filter first, then budget); every cell's
  //    SURFACE stays the full fit — only the points replay. ────────────────────
  const rankMap = useMemo(() => chronoRankMap(eraRows ?? []), [eraRows]);
  const rankTotal = rankMap?.size ?? 0;
  const [budget, setBudget] = useState<number | null>(null); // null = all
  useEffect(() => { setBudget(null); }, [gpField?.fingerprint, rankTotal]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveBudget = Math.min(budget ?? rankTotal, rankTotal);
  const budgetActive = rankMap !== null && effectiveBudget < rankTotal;
  const budgetRows = useMemo(
    () => (eraRows ? filterRowsByBudget(eraRows, rankMap, effectiveBudget) : eraRows),
    [eraRows, rankMap, effectiveBudget],
  );

  const { runsByKey, stalksByKey, hullsByKey } = useMemo(() => {
    const runsMap = new Map<string, ProjectedRun[]>();
    const stalksMap = new Map<string, TransportStalk[]>();
    const hullsMap = new Map<string, Array<{ x: number; y: number }>>();
    // ONE GP-at-own-condition eval per row, shared across every pair cell
    // (FXC-9009 perf): continuous fill campaigns give every row a unique value
    // on every varying axis, so the corrected transport's own-side eval —
    // plane-independent for such complete rows — would otherwise re-walk the
    // interpolation hypercube once per cell (~k²/2 times per row).
    const ownEvals = new Map<Record<string, unknown>, number | null>();
    for (const pair of pairs) {
      const raw = projectRuns(budgetRows, pair.axisX, pair.axisY, quantity);
      const moded = gpField && evidenceMode !== "raw"
        ? applyEvidenceMode(gpField, raw, { axisX: pair.axisX, axisY: pair.axisY, held, mode: evidenceMode, ownEvals })
        : raw;
      if (gpField && evidenceMode === "corrected") {
        stalksMap.set(pair.key, buildTransportStalks(raw, moded));
      }
      // data-support hull: convex hull of the IN-SLICE raw runs for this pair —
      // beyond it the cell's surface is pure model (mode-independent).
      if (gpField && raw.length) {
        const inSlice = applyEvidenceMode(gpField, raw, { axisX: pair.axisX, axisY: pair.axisY, held, mode: "compatible" });
        const hull = convexHull2d(inSlice.map((run) => ({ x: run.x, y: run.y })));
        if (hull.length) hullsMap.set(pair.key, hull);
      }
      runsMap.set(pair.key, runFilter ? runFilter(moded, pair.axisX, pair.axisY) : moded);
    }
    return { runsByKey: runsMap, stalksByKey: stalksMap, hullsByKey: hullsMap };
  }, [pairs, budgetRows, quantity, gpField, evidenceMode, held, runFilter]);

  // ── GP training-point highlight ("which 500", FXC-9009): amber = the run
  //    actually trained the GP; dim = spectator. Just a colour mode — composes
  //    with the era/budget filters, which act on the ROWS upstream. ────────────
  const trainedKeys = useMemo(
    () => new Set(gpField?.trained_condition_keys ?? []),
    [gpField],
  );
  const [highlightTraining, setHighlightTraining] = useState(false);
  useEffect(() => { setHighlightTraining(false); }, [gpField?.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorsByKey = useMemo(() => {
    const map = new Map<string, Array<[number, number, number]>>();
    if (!highlightTraining || !trainedKeys.size) return map;
    for (const [key, runs] of runsByKey) map.set(key, trainingPointColors(runs, trainedKeys));
    return map;
  }, [highlightTraining, trainedKeys, runsByKey]);

  // aggregate transport readout for the footnote (corrected mode)
  const transportInfo = useMemo(() => {
    if (evidenceMode !== "corrected") return null;
    const all: TransportStalk[] = [];
    for (const stalks of stalksByKey.values()) all.push(...stalks);
    return transportStats(all);
  }, [evidenceMode, stalksByKey]);

  // shared orbit view for all cells (drag anywhere on the matrix rotates them all)
  const [view, setView] = useState(CELL_VIEW);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cellRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dragRef = useRef<{ x: number; y: number; view: { pitch: number; yaw: number } } | null>(null);

  // ── IA (FXC-9009): the pre-plot chrome collapses to two slim rows + a
  //    Filters drawer (closed by default). Era chips are GROUPED (same-day
  //    sessions merged, ≤6 primary + "+N more" expander — the latest era is
  //    always a primary chip: it's the live campaign). ─────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAllEras, setShowAllEras] = useState(false);
  const eraGroups = useMemo(
    () => (eraData ? groupEras(eraData.eras) : { primary: [], overflow: [] }),
    [eraData],
  );
  const groupActive = useCallback(
    (group: EraGroup) => group.eraIds.some((id) => !hiddenEras.has(id)),
    [hiddenEras],
  );
  const toggleEraGroup = useCallback((group: EraGroup) => {
    setHiddenEras((cur) => {
      const next = new Set(cur);
      const active = group.eraIds.some((id) => !cur.has(id));
      for (const id of group.eraIds) {
        if (active) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  const activeFilterCount = hiddenAxes.size + hiddenEras.size;

  // EXHAUSTIVE render states — never a silent null (FXC-9009 live bug): v5's
  // isLoading excludes pending-not-fetching (disabled/paused) queries, which
  // previously fell through to `return null` without signalling the fallback.
  if (!queryEnabled) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-correlogram-blocked"><Icons.Refresh size={16} />Waiting for an output selection…</div>;
  }
  if (query.isPending) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-correlogram-loading"><Icons.Refresh size={16} />Loading Tailor field…</div>;
  }
  if (query.isError) {
    // onUnavailable(true, "error") fired — parent swaps to the binned matrix
    // and re-probes; this note only bridges the swap.
    return <div className="v5-adbc-chart-empty" data-testid="gp-correlogram-error"><Icons.Info size={16} />Tailor field fetch failed — using the binned matrix, will retry…</div>;
  }
  if (!available || !gpField) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-correlogram-nofield"><Icons.Info size={16} />No baked Tailor field for {quantity} — using the binned matrix…</div>;
  }
  if (varyingDomains.length < 2) {
    return <div className="v5-adbc-chart-empty" data-testid="gp-correlogram-degenerate">Tailor field varies on fewer than two axes — nothing to correlate in high-D.</div>;
  }

  return (
    <div data-testid="tailor-gp-correlogram" className="v5-adbc-gpc">
      {/* ── ROW 1: the compact always-visible strip ─────────────────────────── */}
      <div className="v5-adb-tailor-strip" style={{ marginBottom: 6, alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span className="v5-adbc-panel-note v5-adbc-gpc-note" data-testid="gpc-header-note" title={`Tailor residual-GP · ${pairs.length} pairwise slices · sliced live${gpField.prior_share != null ? ` · prior ${Math.round(gpField.prior_share * 100)}%` : ""}${gpField.n_residual != null && gpField.hf_solves != null && gpField.hf_solves > gpField.n_residual ? ` · GP trained on ${gpField.n_residual.toLocaleString()} of ${gpField.hf_solves.toLocaleString()} solves (pipeline cap)` : ""}`}>
          <Icons.Sliders size={12} /> Tailor residual-GP · {pairs.length} pairwise slice{pairs.length === 1 ? "" : "s"} · sliced live
          {gpField.prior_share != null ? ` · prior ${Math.round(gpField.prior_share * 100)}%` : ""}
          {/* honest training cap: never flatter bulk */}
          {gpField.n_residual != null
            ? gpField.hf_solves != null && gpField.hf_solves > gpField.n_residual
              ? ` · GP trained on ${gpField.n_residual.toLocaleString()} of ${gpField.hf_solves.toLocaleString()} solves (pipeline cap)`
              : ` · ${gpField.n_residual} residual pts`
            : ""}
          {gpField.stale ? " · ⚠ stale" : ""}
        </span>
        {/* budget replay: first-N runs chronologically across ALL cells */}
        {rankMap ? (
          <label className="v5-adb-gp-held" data-testid="gpc-budget" title="Budget replay: show only the first N runs chronologically in every cell — the surfaces stay the full fit.">
            <span className="v5-adb-gp-held-label">Budget replay</span>
            <input
              type="range"
              min={1}
              max={rankTotal}
              step={1}
              value={effectiveBudget}
              data-testid="gpc-budget-slider"
              onChange={(e) => setBudget(Number(e.target.value))}
              style={{ width: 84 }}
            />
            <span className="v5-adb-gp-held-val">{effectiveBudget}/{rankTotal}</span>
          </label>
        ) : null}
        {/* GP training-point highlight: which runs the fit ACTUALLY saw */}
        {trainedKeys.size ? (
          <button
            type="button"
            className={`v5-adb-chip${highlightTraining ? " is-active" : ""}`}
            data-testid="gpc-highlight-training"
            aria-pressed={highlightTraining}
            title={`Highlight the ${trainedKeys.size.toLocaleString()} runs that actually trained the GP (amber) against the untrained spectators (dim) in every cell.`}
            onClick={() => setHighlightTraining((cur) => !cur)}
          >
            GP training pts ({trainedKeys.size.toLocaleString()})
          </button>
        ) : null}
        {/* the Filters drawer keeps the era/axis/geometry chrome off-screen */}
        <button
          type="button"
          className={`v5-adb-chip${drawerOpen ? " is-active" : ""}`}
          data-testid="gpc-filters-toggle"
          aria-expanded={drawerOpen}
          title="Era filters, axis show/hide, fixed geometry, and the influence ranking."
          onClick={() => setDrawerOpen((cur) => !cur)}
        >
          Filters{activeFilterCount ? ` (${activeFilterCount} active)` : ""}
        </button>
        <button
          type="button"
          className="v5-adb-chip"
          data-testid="gpc-reset-view"
          onClick={() => setView(CELL_VIEW)}
        >
          <Icons.Refresh size={11} /> Reset 3D
        </button>
      </div>

      {/* ── ROW 2: the held sliders — the interactive heart, one compact row ── */}
      <div className="v5-adb-gp-helds v5-adb-gp-helds--row" data-testid="gpc-held-sliders">
        {orderedVaryingDomains.map((d) => (
          <HeldSlider
            key={d.axis}
            axis={d.axis}
            label={label(d.axis)}
            values={d.values}
            value={Number.isFinite(held[d.axis]) ? held[d.axis]! : axisMidpoint(d.values)}
            onChange={(v) => setHeld((cur) => ({ ...cur, [d.axis]: v }))}
          />
        ))}
      </div>

      {/* ── FILTERS DRAWER (collapsed by default) ───────────────────────────── */}
      {drawerOpen ? (
        <div className="v5-adbc-gpc-drawer" data-testid="gpc-filters-drawer">
          {/* data eras: grouped chips (same-day sessions merged, ≤6 + expander) */}
          {eraData ? (
            <div className="v5-adbc-gpc-drawer-row">
              <span className="v5-adbc-gpc-drawer-label" title="Data eras — heuristic gap-based session clustering of run created_at timestamps (gap > 6h starts a new era). Toggle a chip to hide/show that era's points in every cell; the surfaces stay the full fit.">Eras</span>
              <span className="v5-adb-gp-fixed" style={{ marginBottom: 0 }} data-testid="gpc-eras">
                {(showAllEras ? [...eraGroups.primary, ...eraGroups.overflow].sort((a, b) => a.start - b.start) : eraGroups.primary).map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    className={`v5-adb-chip${groupActive(group) ? " is-active" : ""}`}
                    data-testid={`gpc-era-${group.id}`}
                    aria-pressed={groupActive(group)}
                    title={group.eraIds.length > 1 ? `${group.eraIds.length} same-day sessions merged` : undefined}
                    onClick={() => toggleEraGroup(group)}
                  >
                    {group.label} ({group.count.toLocaleString()})
                  </button>
                ))}
                {eraGroups.overflow.length ? (
                  <button
                    type="button"
                    className="v5-adb-chip"
                    data-testid="gpc-eras-more"
                    onClick={() => setShowAllEras((cur) => !cur)}
                  >
                    {showAllEras ? "Show fewer" : `+${eraGroups.overflow.length} more`}
                  </button>
                ) : null}
              </span>
            </div>
          ) : null}
          {/* axis visibility: toggle chips in influence order (|r| shown inline —
              this IS the influence ranking, no duplicate note row) */}
          <div className="v5-adbc-gpc-drawer-row">
            <span className="v5-adbc-gpc-drawer-label" title="Hide an axis's pairs from the matrix. The axis stays HELD — its slider remains. Values = |r| influence on the output.">Axes</span>
            <span className="v5-adb-gp-fixed" style={{ marginBottom: 0 }} data-testid="gpc-axis-toggles">
              {influence.map((item) => {
                const hidden = hiddenAxes.has(item.axis);
                const atMinimum = !hidden && shownAxes.length <= 2;
                return (
                  <button
                    key={item.axis}
                    type="button"
                    className={`v5-adb-chip${hidden ? "" : " is-active"}`}
                    data-testid={`gpc-axis-toggle-${item.axis}`}
                    aria-pressed={!hidden}
                    title={hidden
                      ? `Show ${label(item.axis)}'s pairs again`
                      : atMinimum
                        ? "At least two axes must stay shown"
                        : `Hide ${label(item.axis)}'s pairs. The axis stays held — its slider remains.`}
                    onClick={() => toggleAxis(item.axis)}
                  >
                    {label(item.axis)}{item.r !== null ? ` ${Math.abs(item.r).toFixed(2)}` : ""}
                  </button>
                );
              })}
            </span>
          </div>
          {/* geometry axes — fixed, single design; cannot slide without re-baking */}
          {fixedDomains.length ? (
            <div className="v5-adbc-gpc-drawer-row">
              <span className="v5-adbc-gpc-drawer-label">Geometry</span>
              <span className="v5-adb-gp-fixed" style={{ marginBottom: 0 }} data-testid="gpc-fixed-chips">
                {fixedDomains.map((d) => (
                  <span
                    key={d.axis}
                    className="v5-adb-chip is-fixed"
                    data-testid={`gpc-fixed-${d.axis}`}
                    aria-disabled="true"
                    title={`${label(d.axis)} is fixed at ${d.values[0]} — a single geometry design. Slicing over it would need the field re-baked over that axis.`}
                  >
                    <Icons.Lock size={11} /> {label(d.axis)} = {d.values[0]}
                  </span>
                ))}
              </span>
            </div>
          ) : null}
          {/* the one-line influence note (values also live on the Axes chips) */}
          <div className="v5-adbc-panel-note v5-adbc-gpc-note" data-testid="gpc-influence-note" title={rankedByData ? `ordered by influence on ${quantity} (|r|)` : `axis order = field storage order (not enough rows to rank by influence on ${quantity})`}>
            {rankedByData ? (
              <>
                ordered by influence on {quantity} (|r|):{" "}
                {shownInfluence.map((item, index) => (
                  <span key={item.axis} data-testid={`gpc-influence-${item.axis}`}>
                    {index > 0 ? " · " : ""}
                    {label(item.axis)} {item.r === null ? "–" : Math.abs(item.r).toFixed(2)}
                  </span>
                ))}
                {hiddenAxes.size ? ` (${hiddenAxes.size} axis${hiddenAxes.size === 1 ? "" : "es"} hidden)` : ""}
              </>
            ) : (
              <>
                axis order = field storage order (not enough rows to rank by influence on {quantity})
                {hiddenAxes.size ? ` (${hiddenAxes.size} axis${hiddenAxes.size === 1 ? "" : "es"} hidden)` : ""}
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* the pairwise matrix of smooth GP cells over one scissored WebGL layer.
          The HOST scrolls (own overflow) and the grid fills the full width. */}
      <div
        className="v5-adbc-gpc-host"
        data-testid="gpc-host"
        style={{ position: "relative", maxHeight: "72vh", overflowY: "auto" }}
      >
        <div
          ref={rootRef}
          className="v5-adbc-gpc-grid"
          data-testid="gpc-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 8,
            position: "relative",
            touchAction: "none",
            userSelect: "none",
            cursor: "grab",
          }}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            dragRef.current = { x: e.clientX, y: e.clientY, view };
          }}
          onPointerMove={(e) => {
            const drag = dragRef.current;
            if (!drag) return;
            setView({
              yaw: drag.view.yaw + (e.clientX - drag.x) * 0.01,
              pitch: Math.max(-1.2, Math.min(1.2, drag.view.pitch + (e.clientY - drag.y) * 0.008)),
            });
          }}
          onPointerUp={(e) => {
            dragRef.current = null;
            if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
              (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
            }
          }}
          onPointerCancel={() => { dragRef.current = null; }}
        >
          {pairs.map((pair) => (
            <CellTile
              key={pair.key}
              cellKey={pair.key}
              xLabel={label(pair.axisX)}
              yLabel={label(pair.axisY)}
              cellRefs={cellRefs}
            />
          ))}
          <CorrelogramThreeLayer
            slices={slices}
            runsByKey={runsByKey}
            stalksByKey={stalksByKey}
            hullsByKey={hullsByKey}
            colorsByKey={colorsByKey}
            cellRefs={cellRefs}
            rootRef={rootRef}
            view={view}
          />
        </div>
      </div>

      <div className="v5-adbc-panel-note" style={{ marginTop: 8 }} data-testid="gpc-footnote">
        Every cell = Tailor GP slice over that axis pair at the shared held values · faded = unsupported by data (high sigma) · gold outline = data-support hull · spheres = {quantity} runs ·{" "}
        {evidenceMode === "raw"
          ? "raw points at measured values"
          : evidenceMode === "compatible"
            ? "compatible points only (near the held slice)"
            : "points transported onto the held slice (GP correction, stalks = raw→corrected)"}
        {highlightTraining && trainedKeys.size ? ` · amber = in GP fit (${trainedKeys.size.toLocaleString()}) · dim = spectator` : ""}
        {transportInfo ? ` · transport: mean ${formatTick(transportInfo.mean)} · max ${formatTick(transportInfo.max)}` : ""}
        {eraFiltered && rows && eraRows ? ` · points filtered: ${eraRows.length} of ${rows.length} rows shown (era filter)` : ""}
        {budgetActive ? ` · points = first ${effectiveBudget} of ${rankTotal} (budget replay; surfaces = full fit)` : ""}{" "}
        · drag to rotate all cells
      </div>
    </div>
  );
}

export default TailorGpCorrelogram;
